import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Users, Mail, Loader2 } from 'lucide-react';
import { Button } from '../../components';
import { MemberList } from '../team/MemberList';
import { InviteList } from '../team/InviteList';
import { InviteMemberModal } from '../modals/InviteMemberModal';
import { TransferOwnershipModal } from '../modals/TransferOwnershipModal';
import { useAuth } from '../../contexts/AuthContext';
import { useTeam } from '../../api/hooks/useTeams';

export function CollaboratorsTab() {
  const { user } = useAuth();
  const currentTeamId = user?.current_team_id;

  const { data: team, isLoading } = useTeam(currentTeamId ?? undefined);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const isOwner = team?.user_role === 'owner';
  const isAdmin = team?.user_role === 'admin' || isOwner;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentTeamId || !team) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">No Team</h2>
        <p className="text-text-secondary">
          You're not part of a team yet. A team is created automatically when you sign up.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Members Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background-card rounded-xl border border-border"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-text-secondary" />
            <h3 className="font-medium text-text-primary">
              Members ({team.members?.length || 0})
            </h3>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowInviteModal(true)} icon={<Plus size={16} />}>
              Invite
            </Button>
          )}
        </div>
        <MemberList
          members={team.members || []}
          currentUserId={user?.id || 0}
          userRole={team.user_role || 'member'}
          teamId={team.id}
          onTransferOwnership={() => setShowTransferModal(true)}
        />
      </motion.div>

      {/* Pending Invites Section */}
      {isAdmin && team.pending_invites && team.pending_invites.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-background-card rounded-xl border border-border"
        >
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Mail size={18} className="text-text-secondary" />
            <h3 className="font-medium text-text-primary">
              Pending Invites ({team.pending_invites.length})
            </h3>
          </div>
          <InviteList invites={team.pending_invites} teamId={team.id} />
        </motion.div>
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteMemberModal
          teamId={team.id}
          onClose={() => setShowInviteModal(false)}
        />
      )}
      {showTransferModal && team.members && (
        <TransferOwnershipModal
          teamId={team.id}
          members={team.members.filter(m => m.id !== user?.id)}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </div>
  );
}
